import React, { useCallback } from 'react';

import { Tb123 } from 'react-icons/tb';

import dayjs from 'dayjs';

import Typography from 'components/typography/typography';

import Button from 'components-new/button/button';

import useConvertJobToUrgent from 'state/remote/remote-organization/use-convert-job-to-urgent';

import type { DerivedJobAd } from 'state/__generated__/tadaahAPI';
import { getApiOrganisationPeriodJobsAssignmentDetailsListQueryKey } from 'state/__generated__/tadaahAPI';
import Modal from 'components-new/modal/modal';
import client from 'state/remote/client';

import { createLocale } from 'locales';
import { showSuccessToast } from 'components/toast/toast';

const localize = createLocale('organisationPeriodJobsLocales');
const globalLocalize = createLocale('global');

interface ConvertToUrgentDialogProps {
  periodJobId: string;
  assignment: DerivedJobAd;
  isOpen?: boolean;
  onCloseClicked: () => void;
}
const ConvertToUrgentDialog: React.FC<ConvertToUrgentDialogProps> = (props) => {
  const { assignment, isOpen, onCloseClicked, periodJobId } = props;
  const convertCallback = useCallback(() => {
    client.invalidateQueries(
      getApiOrganisationPeriodJobsAssignmentDetailsListQueryKey(
        periodJobId.toString() || '',
      )?.[0],
    );
    showSuccessToast({
      description: localize('jobConverted'),
      title: globalLocalize('done'),
    });
  }, [periodJobId]);
  const { convertToUrgent } = useConvertJobToUrgent(convertCallback);

  const onConfirmClicked = useCallback(() => {
    convertToUrgent({ jobId: assignment.id?.toString() || '' });
    onCloseClicked?.();
  }, [assignment.id, convertToUrgent, onCloseClicked]);

  return (
    <Modal
      variant="primary"
      icon={Tb123}
      isOpen={isOpen}
      onClose={onCloseClicked}
      title={localize('confirmAction')}
      footer={{
        helpText: localize('clickConvertToProceed'),
        primaryButton: (
          <Button
            variant="primary"
            label={localize('convert')}
            onClick={onConfirmClicked}
            className="w-full sm:w-auto"
          />
        ),
        secondaryButton: (
          <Button
            variant="secondary"
            label={localize('back')}
            onClick={onCloseClicked}
            className="w-full sm:w-auto"
          />
        ),
      }}
    >
      <>
        <Typography
          as="p"
          variant="body-large-bold"
          className="text-gray-600 my-3"
        >
          {localize('confirmConvertJob')}
        </Typography>
        <div className=" flex items-center gap-y-6 gap-x-3 w-full  justify-between">
          <div className="flex flex-col gap-y-2  text-gray400">
            <Typography as="p" variant="body-large-bold">
              {localize('location')}
            </Typography>
            <Typography as="p" variant="body-large-bold">
              {localize('time')}
            </Typography>
            <Typography as="p" variant="body-large-bold">
              {localize('date')}
            </Typography>
          </div>
          <div className="flex flex-col gap-y-[0.850rem] text-gray600">
            <Typography as="p" variant="body-medium">
              {assignment.location.name}
            </Typography>
            <Typography as="p" variant="body-medium">
              {`${dayjs(assignment.start, 'mm:ss').format('mm:ss')} - ${dayjs(
                assignment.end,
                'mm:ss',
              ).format('mm:ss')}`}
            </Typography>
            <Typography as="p" variant="body-medium">
              {assignment.date}
            </Typography>
          </div>
        </div>
      </>
    </Modal>
  );
};

export default ConvertToUrgentDialog;
